import React from "react";
import RecordManager from "../../../components/founder/RecordManager";
import { SectionCard, Empty } from "../../../components/founder/ui";
import api from "../../../lib/api";
import { toast } from "sonner";
import { Check, Plus } from "lucide-react";

const RESEARCH_AREAS = ["Demand", "Conversion", "Capacity", "Economics", "Customer Experience", "Reputation", "Systems", "Owner Dependence", "Growth Assets", "Other"];

function BriefBlock({ label, children }) {
  return (
    <div className="mb-4">
      <div className="text-xs uppercase tracking-wide text-lr-clay mb-1">{label}</div>
      <div className="text-lr-charcoal text-sm leading-relaxed">{children}</div>
    </div>
  );
}

export default function InvestigationTab({ ws, bid, reload }) {
  const brief = ws.brief;
  const notes = ws.notes;

  // Which brief items have already been promoted into Diagnosis records (dedupe by brief_ref).
  const promotedRefs = new Set(
    [...(ws.constraints || []), ...(ws.assets || [])].map((r) => r.brief_ref).filter(Boolean)
  );

  const promote = async (resource, briefRef, body) => {
    try {
      await api.post(`/businesses/${bid}/${resource}`, {
        ...body, include_in_blueprint: false, source: "Investigation Brief", brief_ref: briefRef,
      });
      toast.success("Added to Diagnosis");
      reload();
    } catch { toast.error("Could not add to Diagnosis"); }
  };

  const PromoteRow = ({ text, briefRef, resource, body, addLabel }) => {
    const done = promotedRefs.has(briefRef);
    return (
      <li className="flex items-start justify-between gap-3 py-1.5">
        <span className="text-sm text-lr-charcoal">{text}</span>
        {done ? (
          <span className="text-[11px] text-lr-olive whitespace-nowrap flex items-center gap-1" data-testid="promote-done">
            <Check className="h-3.5 w-3.5" /> In Diagnosis
          </span>
        ) : (
          <button onClick={() => promote(resource, briefRef, body)} data-testid={`promote-${resource}`}
            className="text-[11px] whitespace-nowrap px-2 py-0.5 rounded-[3px] border border-lr-olive/40 text-lr-olive hover:bg-lr-olive/10 flex items-center gap-1">
            <Plus className="h-3 w-3" /> {addLabel}
          </button>
        )}
      </li>
    );
  };

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <SectionCard title="Investigation Brief" testid="investigation-brief">
        {!brief ? <Empty>Auto-generated once the assessment is submitted.</Empty> : (
          <div>
            <p className="text-xs text-lr-leather mb-4 italic">Structured starting point for investigation, not a final diagnosis. Promote useful items into editable Diagnosis records. Promotion never confirms or includes anything.</p>
            <BriefBlock label="Owner Objective">{brief.owner_objective || "Not recorded"}</BriefBlock>
            <BriefBlock label="Owner Stated Biggest Problem">{brief.owner_stated_biggest_problem || "Not recorded"}</BriefBlock>
            <BriefBlock label="If I Could Just Fix…">{brief.if_could_just_fix || "Not recorded"}</BriefBlock>
            <BriefBlock label="Preliminary Demand Safety">{brief.preliminary_demand_safety}</BriefBlock>
            <BriefBlock label="Known Client-Reported Constraints">
              {brief.known_client_reported_constraints?.length ? (
                <ul className="divide-y hairline">
                  {brief.known_client_reported_constraints.map((c, i) => (
                    <PromoteRow key={i} text={c} briefRef={`cc::${c}`} resource="constraints" addLabel="Add as Candidate Constraint"
                      body={{ name: c, type: "Client-reported constraint", status: "Candidate", confidence: "Low", owner_awareness: "Known" }} />
                  ))}
                </ul>
              ) : "Not recorded"}
            </BriefBlock>
            <BriefBlock label="Signals">
              <div className="flex flex-wrap gap-1.5">{(brief.signals || []).map((s) => <span key={s} className="text-[11px] px-1.5 py-0.5 rounded-[3px] bg-lr-leather/12 text-lr-leather">{s}</span>)}</div>
            </BriefBlock>
            <BriefBlock label="Owner Dependence Indicators">
              {brief.owner_dependence_indicators?.length ? (
                <ul className="divide-y hairline">
                  {brief.owner_dependence_indicators.map((x, i) => (
                    <PromoteRow key={i} text={x} briefRef={`dep::${x}`} resource="constraints" addLabel="Add as Candidate Constraint"
                      body={{ name: x, type: "Owner Dependence", status: "Candidate", confidence: "Low", owner_awareness: "Known" }} />
                  ))}
                </ul>
              ) : "Not recorded"}
            </BriefBlock>
            <BriefBlock label="Open Loop Indicators">{brief.open_loop_indicators?.join(", ") || "Not recorded"}</BriefBlock>
            <BriefBlock label="Potential Underused Assets">
              {brief.potential_underused_assets?.length ? (
                <ul className="divide-y hairline">
                  {brief.potential_underused_assets.map((x, i) => (
                    <PromoteRow key={i} text={x} briefRef={`asset::${x}`} resource="assets" addLabel="Add as Underused Asset"
                      body={{ description: x }} />
                  ))}
                </ul>
              ) : "Not recorded"}
            </BriefBlock>
            <BriefBlock label="What Owner Has Tried">{brief.what_owner_has_tried || "Not recorded"}</BriefBlock>
            <BriefBlock label="What Owner Does NOT Want">{brief.what_owner_does_not_want || "Not recorded"}</BriefBlock>
            <BriefBlock label="External Research Priorities"><ul className="list-disc pl-4">{(brief.external_research_priorities || []).map((x, i) => <li key={i}>{x}</li>)}</ul></BriefBlock>
            <BriefBlock label="Questions Needing Clarification">{brief.questions_needing_clarification?.length ? <ul className="list-disc pl-4">{brief.questions_needing_clarification.map((x, i) => <li key={i}>{x}</li>)}</ul> : "Not recorded"}</BriefBlock>
            {brief.founder_followup_required && <div className="mt-2 text-sm text-[#8f4a49] font-medium">⚑ Founder follow-up required (client asked to explain by phone)</div>}
          </div>
        )}
      </SectionCard>

      <div className="lr-card p-5">
        <RecordManager
          resource="notes" bid={bid} items={notes.map((n) => n)} testid="investigation-notes"
          title="Investigation Notes" addLabel="Add note" onChange={reload}
          fields={[
            { name: "area", label: "Research area", type: "select", options: RESEARCH_AREAS },
            { name: "text", label: "Note", type: "textarea", rows: 4 },
          ]}
          renderItem={(n) => (
            <div>
              {n.area && <div className="text-xs uppercase tracking-wide text-lr-clay mb-1">{n.area}</div>}
              <div className="text-lr-charcoal text-sm whitespace-pre-wrap">{n.text}</div>
            </div>
          )}
        />
        <p className="text-xs text-lr-leather mt-3">Areas are guides, not requirements. Follow an anomaly wherever it leads.</p>
      </div>
    </div>
  );
}
